import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { GraduationCap, Eye, EyeOff, Loader2, ArrowLeft, Building2 } from 'lucide-react';
import Footer from '../../components/layout/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StudentLogin() {
  const navigate = useNavigate();
  const [studentId, setStudentId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(true);

  // Get tenant code from URL path
  const getTenantCode = () => {
    const pathParts = window.location.pathname.split('/');
    // URL pattern: /tenant-code/student/login or just /student/login
    if (pathParts.length > 2 && pathParts[2] === 'student') {
      return pathParts[1];
    }
    // Check if first part is a tenant code (not 'student')
    if (pathParts[1] && pathParts[1] !== 'student') {
      return pathParts[1];
    }
    // Try from localStorage
    return localStorage.getItem('tenant_code') || null;
  };

  const tenantCode = getTenantCode();

  useEffect(() => {
    if (tenantCode) {
      fetchTenantInfo();
    } else {
      setTenantLoading(false);
    }
  }, [tenantCode]);

  const fetchTenantInfo = async () => {
    try {
      setTenantLoading(true);
      const response = await fetch(`${API_URL}/api/tenants/by-subdomain/${tenantCode}`);
      if (response.ok) {
        const data = await response.json();
        setTenant(data);
      }
    } catch (err) {
      console.error('Error fetching tenant info:', err);
    } finally {
      setTenantLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!studentId.trim() || !password.trim()) {
      toast.error('Please enter Student ID and Password');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/student-portal/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          student_id: studentId.trim().toUpperCase(),
          password: password,
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }
      
      // Store token and student info
      localStorage.setItem('studentToken', data.access_token);
      localStorage.setItem('studentInfo', JSON.stringify({
        student_id: data.student_id,
        student_name: data.student_name,
        status: data.status,
        admission_type: data.admission_type,
        tenant_code: tenantCode,
      }));
      if (tenantCode) {
        localStorage.setItem('student_tenant_code', tenantCode);
      }
      
      toast.success(`Welcome, ${data.student_name}!`);
      
      // Build the redirect path with tenant code if available
      const basePath = tenantCode ? `/${tenantCode}` : '';
      
      // Small delay to ensure localStorage is saved before redirect
      setTimeout(() => {
        // Redirect based on status
        if (data.status === 'Withdrawn') {
          window.location.href = `${basePath}/student/withdrawn`;
        } else if (data.status === 'booked') {
          window.location.href = `${basePath}/student/form-fill`;
        } else {
          window.location.href = `${basePath}/student/dashboard`;
        }
      }, 100);
      
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToHome = () => {
    if (tenantCode) {
      window.location.href = `/${tenantCode}`;
    } else {
      window.location.href = '/';
    }
  };

  if (tenantLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4 relative">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center pb-1 pt-5">
          {/* Tenant Logo & Name */}
          {tenant ? (
            <>
              {tenant.logo_url ? (
                <img 
                  src={tenant.logo_url} 
                  alt={tenant.name} 
                  className="h-12 w-auto object-contain mx-auto mb-2"
                />
              ) : (
                <div className="mx-auto w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center mb-2">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
              )}
              <h2 className="text-base font-semibold text-gray-700 mb-1">{tenant.name}</h2>
            </>
          ) : (
            <div className="mx-auto w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center mb-2">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
          )}
          <CardTitle className="text-xl font-bold text-gray-800">Student Portal</CardTitle>
          <CardDescription className="text-gray-600 text-sm">
            Login with your Student ID and Password
          </CardDescription>
        </CardHeader>
        
        <CardContent className="pt-2 pb-5">
          <form onSubmit={handleLogin} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="studentId" className="text-sm">Student ID</Label>
              <Input
                id="studentId"
                type="text"
                placeholder="e.g., ILOEBCA2026001"
                value={studentId}
                onChange={(e) => setStudentId(e.target.value.toUpperCase())}
                className="uppercase h-10"
                data-testid="student-id-input"
              />
            </div>
            
            <div className="space-y-1">
              <Label htmlFor="password" className="text-sm">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Your mobile number"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-10"
                  data-testid="student-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500">
                For first login, your mobile number is the password
              </p>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-indigo-600 hover:bg-indigo-700 h-10"
              disabled={isLoading}
              data-testid="student-login-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </Button>
          </form>
          
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-600">
              Having trouble? Contact college office
            </p>
            <button 
              onClick={handleBackToHome}
              className="text-sm text-indigo-600 hover:text-indigo-700 mt-2 inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </button>
          </div>
        </CardContent>
      </Card>
      
      {/* Footer */}
      <div className="absolute bottom-4 left-0 right-0">
        <Footer variant="minimal" />
      </div>
    </div>
  );
}
